import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { FormData, User } from "../core/_models";
import CustomFormSearchSelect from "../../apps/components/CustomFormSearchSelect";
import CustomFormInput from "../../apps/components/CustomFormInput";
import { useSnackbar } from "../../../../_theme/context/SnackbarContext";
import { createUser, updateUser } from "../core/_request";
import { getApiErrorMessage } from "../../../../utils/utils";

type Props = {
  show: boolean;
  onClose: () => void;
  onCreateUser: () => void;
  selectedData: User | null
  isUpdateMode: boolean
};


const FIELD_MAX_LENGTHS: Partial<Record<keyof FormData, number>> = {
  first_name: 50,
  last_name: 50,
};

const FIELD_MAX_LENGTH_LABELS: Partial<Record<keyof FormData, string>> = {
  first_name: "First name",
  last_name: "Last name",
};

const getMaxLengthMessage = (field: keyof FormData): string | undefined => {
  const maxLength = FIELD_MAX_LENGTHS[field];
  if (!maxLength) {
    return undefined;
  }

  const label = FIELD_MAX_LENGTH_LABELS[field] ?? field;
  return `${label} must be ${maxLength} characters or less`;
};

const CreateUserModal: React.FC<Props> = ({ show, onClose, onCreateUser, selectedData, isUpdateMode }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { showSnackbar } = useSnackbar();

  const initialForm: FormData = {
    first_name: selectedData?.first_name || "",
    last_name: selectedData?.last_name || "",
    title: selectedData?.title || "",
    gender: selectedData?.gender || "",
  };

  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const resetError = (name: string) => {
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };


  const handleCreateUser = async (formData: FormData) => {
    setLoading(true);
    if (isUpdateMode) {
      try {
        const resp = await updateUser({ ...formData, email: selectedData?.email });
        if (resp.status === "ok") {
          onClose();
          showSnackbar(resp.message, "success");
          onCreateUser();
        } else {
          showSnackbar(resp.message, "danger");
        }
      } catch (error) {
        showSnackbar(getApiErrorMessage(error, "Failed to Update User"), "danger");
        console.error(error);
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const resp = await createUser(formData);
        if (resp.status === "ok") {
          onClose();
          showSnackbar(resp.message, "success");
          onCreateUser();
        } else {
          showSnackbar(resp.message, "danger");
        }
      } catch (error) {
        showSnackbar(getApiErrorMessage(error, "Failed to create User"), "danger");
        console.error(error);
      } finally {
        setLoading(false);
      }
    }


  };
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const field = name as keyof FormData;
    const maxLength = FIELD_MAX_LENGTHS[field];
    const exceededLimit = Boolean(maxLength && value.length > maxLength);

    setForm({ ...form, [name]: maxLength ? value.slice(0, maxLength) : value });

    if (exceededLimit) {
      const message = getMaxLengthMessage(field);
      if (message) {
        setErrors((prev) => ({ ...prev, [name]: message }));
        return;
      }
    }

    resetError(name);
  };
  const handleSelectChange = (name: keyof FormData, value: string | number) => {
    setForm({ ...form, [name]: value });
    resetError(name);
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.first_name) {
      newErrors.first_name = "First name is required";
    } else {
      const maxLength = FIELD_MAX_LENGTHS.first_name;
      const message = getMaxLengthMessage("first_name");
      if (maxLength && message && form.first_name.length > maxLength) {
        newErrors.first_name = message;
      }
    }

    if (!form.last_name) {
      newErrors.last_name = "Last name is required";
    } else {
      const maxLength = FIELD_MAX_LENGTHS.last_name;
      const message = getMaxLengthMessage("last_name");
      if (maxLength && message && form.last_name.length > maxLength) {
        newErrors.last_name = message;
      }
    }

    if (!form.title) newErrors.title = "Title is required";
    if (!form.gender) newErrors.gender = "Gender is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    handleCreateUser(form);
  };

  useEffect(() => {
    if (!show) {
      setForm(initialForm);
      setErrors({});
    }
  }, [show]);


  useEffect(() => {
    if (isUpdateMode) {
      setForm({ first_name: selectedData?.first_name, last_name: selectedData?.last_name, gender: selectedData?.gender, title: selectedData?.title })
    }
  }, [isUpdateMode, selectedData])

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="modal-dialog modal-lg modal-dialog-centered position-relative"
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{isUpdateMode ? "Update User" : "Create User"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label className="required">Title</Form.Label>
                <CustomFormSearchSelect
                  value={form.title || ""}
                  name=""
                  onChange={(value: any) => handleSelectChange("title", value)}
                  options={["Mr", "Ms", "Dr"].map((title) => ({
                    label: title,
                    value: title,
                  }))}
                  placeholder="Select Title"
                  usePortal={true}

                />
                {errors.title && (
                  <div className="text-danger small">{errors.title}</div>
                )}
              </Form.Group>
            </Col>
            <Col md={9}>
              <Form.Group className="mb-3">
                <Form.Label className="required">First Name</Form.Label>
                <CustomFormInput
                  name="first_name"
                  value={form.first_name || ""}
                  onChange={handleChange}
                  placeholder="Enter First name"
                  disabled={isUpdateMode}
                />
                {errors.first_name && (
                  <div className="text-danger small">{errors.first_name}</div>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
            </Col>
            <Col md={9}>
              <Form.Group className="mb-3">
                <Form.Label className="required">Last Name</Form.Label>
                <CustomFormInput
                  name="last_name"
                  value={form.last_name || ""}
                  onChange={handleChange}
                  placeholder="Enter Last name"
                  disabled={isUpdateMode}

                />
                {errors.last_name && (
                  <div className="text-danger small">{errors.last_name}</div>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
            </Col>
            <Col md={9}>
              <Form.Group className="mb-3">
                <Form.Label className="required">Gender</Form.Label>
                <CustomFormSearchSelect
                  value={form.gender || ""}
                  name="gender"
                  onChange={(value: any) => handleSelectChange("gender", value)}
                  options={[{ label: "Male", value: "M" }, { label: "Female", value: "F" }].map((gender) => ({
                    label: gender.label,
                    value: gender.value,
                  }))}
                  placeholder="Select Gender"
                  usePortal={true}
                />
                {errors.gender && (
                  <div className="text-danger small">{errors.gender}</div>
                )}
              </Form.Group>
            </Col>

          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={loading} type="submit" variant="primary">
            {isUpdateMode ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateUserModal;
